import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../../../lib/apiClient';
import { useAuth } from '../../auth/AuthContext';
import { formatBuyType, formatMapName } from '../../liveSession/lib/formatters';
import { formatClock } from '../../liveSession/lib/roundClock';
import { useMyTeam } from '../../team/useMyTeam';
import {
  formatActionType,
  formatStrategyType,
  formatUpdated,
  loadoutLabel,
  planPhaseLabel,
  roleBlurb,
  sideName,
} from '../lib/formatPlaybook';
import { ASSIGN_MARKS_NOTE } from '../../learn/lib/learnView';
import { applyLearning, isKnown } from '../lib/playbook';
import { getStrategy, markRoleLearned, setStrategyLearning, unmarkRoleLearned } from '../strategyApi';
import type { StrategyDetail, StrategyRoleDetail } from '../types';
import PlaybookShell from './PlaybookShell';
import styles from './StrategyDetailPage.module.css';

export default function StrategyDetailPage() {
  const { strategyId = '' } = useParams();
  const [params] = useSearchParams();
  const from = params.get('from') ?? '';
  const returnTo = params.get('return') ?? '';
  const roleQuery = params.get('role') ?? '';
  const fromLearn = from === 'learn';
  const backHref = fromLearn
    ? `/learn${returnTo ? `?${returnTo}` : ''}`
    : `/strategies${from ? `?${from}` : ''}`;
  const backLabel = fromLearn ? 'Back to learn' : 'Back to strategies';
  const assignNote = fromLearn && new URLSearchParams(returnTo).get('as') === 'assign';
  const { user } = useAuth();
  const { team, loading: teamLoading } = useMyTeam(user?.id);
  const [detail, setDetail] = useState<StrategyDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roleIndex, setRoleIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!team?.id || !strategyId) return;
    let cancelled = false;
    setLoading(true);
    setMissing(false);
    setError(null);
    setDetail(null);
    getStrategy(team.id, strategyId)
      .then((strategy) => {
        if (cancelled) return;
        const roles = [...strategy.roles].sort((a, b) => a.sortOrder - b.sortOrder);
        setDetail({
          ...strategy,
          roles,
          planSteps: [...strategy.planSteps].sort((a, b) => a.sortOrder - b.sortOrder),
        });
        const index = roleQuery ? roles.findIndex((role) => role.id === roleQuery) : 0;
        setRoleIndex(index >= 0 ? index : 0);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setMissing(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Could not load strategy');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [team?.id, strategyId, roleQuery]);

  const roles = detail?.roles ?? [];
  const role = roles[roleIndex] ?? roles[0];
  const learnedCount = useMemo(() => roles.filter((item) => item.learned).length, [roles]);
  const known = detail ? isKnown({ roleCount: roles.length, rolesLearned: learnedCount }) : false;

  async function runLearning(action: () => Promise<void>, success: string) {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      setToast(success);
    } catch (err: unknown) {
      setToast(err instanceof Error ? err.message : 'Could not update learning');
    } finally {
      setBusy(false);
    }
  }

  function toggleRole(current: StrategyRoleDetail) {
    if (!team?.id || !detail) return;
    void runLearning(async () => {
      const result = current.learned
        ? await unmarkRoleLearned(team.id, detail.id, current.id)
        : await markRoleLearned(team.id, detail.id, current.id);
      setDetail((prev) => (prev ? applyLearning(prev, result) : prev));
    }, current.learned ? 'Role marked as still to learn.' : 'Role marked as learned.');
  }

  function toggleAll() {
    if (!team?.id || !detail) return;
    const learned = !known;
    void runLearning(async () => {
      const result = await setStrategyLearning(team.id, detail.id, learned);
      setDetail((prev) => (prev ? applyLearning(prev, result) : prev));
    }, learned ? 'Strategy marked as known.' : 'Learning progress reset.');
  }

  return (
    <PlaybookShell crumb="Strategies">
      <Link className={styles.back} to={backHref}>← {backLabel}</Link>

      {(teamLoading || loading) && <p className={styles.status}>Loading strategy…</p>}
      {missing && (
        <div className={styles.missing}>
          <h1>Strategy not found</h1>
          <p>This strategy is not in the team playbook.</p>
          <Link className={styles.primary} to={backHref}>{fromLearn ? 'Back to learn' : 'Browse strategies'}</Link>
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}

      {detail && (
        <>
          <div className={styles.head}>
            <div className={styles.eyebrow}>{formatMapName(detail.map) || detail.map}</div>
            <div className={styles.meta}>
              <span className={`${styles.badge} ${styles[detail.side.trim().toLowerCase()] ?? ''}`}>
                {detail.side.trim().toUpperCase()} side
              </span>
              {detail.strategyType && <span className={styles.badge}>{formatStrategyType(detail.strategyType)}</span>}
              {detail.buyType && <span className={styles.badge}>{formatBuyType(detail.buyType)}</span>}
            </div>
            <h1>{detail.name}</h1>
            {detail.description && <p>{detail.description}</p>}
          </div>

          <div className={styles.grid}>
            <div>
              <section className={styles.panel}>
                <div className={styles.panelHead}>
                  <div>
                    <h2>The round plan</h2>
                    <p>The shared picture before you focus on your role.</p>
                  </div>
                  {detail.site && <span className={styles.micro}>{detail.site} site</span>}
                </div>
                {detail.planSteps.length > 0 && (
                  <div className={styles.plan}>
                    {detail.planSteps.map((step) => {
                      const phase = planPhaseLabel(step.sortOrder);
                      const number = String(step.sortOrder).padStart(2, '0');
                      return (
                        <article key={step.id}>
                          <div className={styles.planNumber}>{phase ? `${number} / ${phase}` : number}</div>
                          <h3>{step.title}</h3>
                          <p>{step.body}</p>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>

              {role && <section className={`${styles.panel} ${styles.roles}`}>
                <div className={styles.panelHead}>
                  <div>
                    <h2>Roles & execution</h2>
                    <p>Pick a role to review its loadout and round timeline.</p>
                  </div>
                  <span className={styles.micro}>{roles.length} roles</span>
                </div>
                <div className={styles.tabs} role="group" aria-label="Select a role">
                  {roles.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-pressed={item.id === role.id}
                      onClick={() => setRoleIndex(index)}
                    >
                      {item.name}
                      <small className={item.learned ? styles.check : undefined}>
                        {item.learned ? 'Learned' : 'To learn'}
                      </small>
                    </button>
                  ))}
                </div>
                <div className={styles.roleBody}>
                  <div className={styles.roleTitle}>
                    <div>
                      <div className={styles.eyebrow}>Role {String(role.sortOrder).padStart(2, '0')}</div>
                      <h3>{role.name}</h3>
                      <p>{roleBlurb(role.name)}</p>
                    </div>
                    <button type="button" aria-pressed={role.learned} disabled={busy} onClick={() => toggleRole(role)}>
                      {role.learned ? 'Learned · Undo' : 'Mark role learned'}
                    </button>
                  </div>
                  <div className={styles.loadout}>
                    <span className={styles.micro}>Loadout</span>
                    <span>{loadoutLabel(role.buyType || detail.buyType)}</span>
                  </div>
                  <ol className={styles.timeline}>
                    {[...role.actions]
                      .sort((a, b) => b.roundTimeSecs - a.roundTimeSecs)
                      .map((action) => (
                        <li key={action.id}>
                          <time>{formatClock(action.roundTimeSecs)}</time>
                          <div>
                            <strong>{formatActionType(action.actionType)}</strong>
                            {action.description && <p>{action.description}</p>}
                            {action.utilityUse && <p className={styles.utility}>{action.utilityUse.name}</p>}
                          </div>
                        </li>
                      ))}
                  </ol>
                </div>
              </section>}
            </div>

            <aside className={styles.side}>
              <section className={styles.panel}>
                <div className={styles.panelHead}><h2>Your knowledge</h2></div>
                <div className={styles.aside}>
                  {assignNote && <p className={styles.assignNote}>{ASSIGN_MARKS_NOTE}</p>}
                  <span className={`${styles.badge} ${known ? styles.known : styles.pending}`}>
                    {known ? 'Known' : learnedCount > 0 ? 'Learning' : 'Not yet known'}
                  </span>
                  <div className={styles.progressNumber}>
                    {learnedCount}<small> / {roles.length} roles</small>
                  </div>
                  <progress max={Math.max(roles.length, 1)} value={learnedCount} aria-label="Roles learned" />
                  <p>
                    {known
                      ? 'You have marked every role as learned. Revisit a role anytime to refresh your memory.'
                      : 'Learn each role to mark this strategy as known. Your progress is just for you.'}
                  </p>
                  {roles.length > 0 && (
                    <button type="button" className={known ? styles.button : styles.primary} disabled={busy} onClick={toggleAll}>
                      {known ? 'Reset learning progress' : `I know all ${roles.length} roles`}
                    </button>
                  )}
                </div>
              </section>

              <section className={styles.panel}>
                <div className={styles.panelHead}><h2>At a glance</h2></div>
                <dl className={styles.facts}>
                  <div><dt>Map</dt><dd>{formatMapName(detail.map) || detail.map}</dd></div>
                  <div><dt>Side</dt><dd>{sideName(detail.side)}</dd></div>
                  {detail.strategyType && <div><dt>Type</dt><dd>{formatStrategyType(detail.strategyType)}</dd></div>}
                  {detail.buyType && <div><dt>Team buy</dt><dd>{formatBuyType(detail.buyType)}</dd></div>}
                  {detail.route && <div><dt>Primary route</dt><dd>{detail.route}</dd></div>}
                  {formatUpdated(detail.updatedAt) && <div><dt>Updated</dt><dd>{formatUpdated(detail.updatedAt)}</dd></div>}
                </dl>
              </section>

              <div className={styles.note}>
                <div className={styles.eyebrow}>From review to round</div>
                <h3>Know the plan before the call.</h3>
                <p>Review the other roles too, so you know who you are trading and whose utility you are waiting for.</p>
              </div>
            </aside>
          </div>
        </>
      )}

      {toast && <div className={styles.toast} role="status">{toast}</div>}
    </PlaybookShell>
  );
}
